import React, { useState, useEffect } from 'react';
import { authApi } from '../services/api';
import { useAuth } from '../contexts/CockpitAuthContext';
import Button from './Button';

import Icone from './Icone';
/**
 * ConfigurarMfaModal — 01/09/2026.
 *
 * Aparece quando o suporte ZEROU a verificação em 2 passos de alguém (a pessoa
 * perdeu o celular/app e não conseguia mais entrar). A conta volta a entrar só
 * com a senha, e esta tela pede a reconfiguração assim que ela entra — o
 * `pendente` vem do /auth/mfa/status e só some quando o novo app é confirmado.
 *
 * Diferente do TrocaSenhaModal, este NÃO bloqueia: quem acabou de recuperar o
 * acesso pode não estar com o celular na mão, e travar o cockpit aqui seria
 * trocar um bloqueio por outro. O pedido volta no próximo login.
 */
export default function ConfigurarMfaModal() {
  const { usuario } = useAuth();
  const [pendente, setPendente] = useState(false);
  const [iniciado, setIniciado] = useState(null); // { secret, qr }
  const [codigo, setCodigo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState('');
  const [pronto, setPronto] = useState(false);

  useEffect(() => {
    if (!usuario || usuario.tipo !== 'escritorio') return;
    // Adiado nesta sessão? Não incomoda de novo até o próximo login.
    if (sessionStorage.getItem('mfa_pendente_adiado') === '1') return;
    authApi.mfaStatus()
      .then(({ data }) => setPendente(!!data?.pendente))
      .catch(() => {});
  }, [usuario]);

  if (!pendente) return null;

  const adiar = () => {
    sessionStorage.setItem('mfa_pendente_adiado', '1');
    setPendente(false);
  };

  const iniciar = async () => {
    setOcupado(true); setErro('');
    try {
      const { data } = await authApi.mfaIniciar();
      setIniciado(data); setCodigo('');
    } catch (err) {
      setErro(err.response?.data?.erro || err.message);
    } finally { setOcupado(false); }
  };

  const confirmar = async () => {
    setOcupado(true); setErro('');
    try {
      await authApi.mfaConfirmar(codigo);
      setPronto(true);
      setTimeout(() => setPendente(false), 2000);
    } catch (err) {
      setErro(err.response?.data?.erro || err.message);
    } finally { setOcupado(false); }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(10, 14, 26, 0.85)',
        backdropFilter: 'blur(12px)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        overflowY: 'auto',
      }}
    >
      <div className="card glass-strong" style={{ maxWidth: 460, width: '100%', padding: 32 }}>
        {pronto ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}><Icone nome="ok" tamanho={30} espessura={1.5} /></div>
            <h2 style={{ margin: '0 0 8px', fontSize: 22 }}>Verificação reativada</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              Da próxima vez, o cockpit vai pedir o código do seu novo app.
            </p>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 36, marginBottom: 12, textAlign: 'center' }}><Icone nome="escudo" tamanho={30} espessura={1.5} /></div>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, textAlign: 'center' }}>
              Reative a verificação em 2 passos
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20, textAlign: 'center' }}>
              Olá, <strong>{usuario?.nome}</strong>! Sua verificação foi zerada pelo suporte pra você
              conseguir entrar. Agora cadastre o app autenticador de novo — sem ele, só a senha protege
              o cockpit.
            </p>

            {erro && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{erro}</div>}

            {!iniciado && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Button variant="primary" onClick={iniciar} disabled={ocupado} style={{ width: '100%' }}>
                  {ocupado ? '…' : 'Configurar agora'}
                </Button>
                <Button variant="ghost" onClick={adiar} style={{ width: '100%' }}>
                  Agora não (o cockpit pede de novo no próximo login)
                </Button>
              </div>
            )}

            {iniciado && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p style={{ fontSize: 13, fontWeight: 600 }}>1. Escaneie o QR code no app autenticador</p>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={iniciado.qr}
                    alt="QR code da verificação em 2 passos"
                    style={{ width: 200, height: 200, background: 'var(--ckd-bg-ffffff, #fff)', padding: 8, borderRadius: 10 }}
                  />
                </div>
                <details>
                  <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>
                    Não consegue escanear? Mostrar código manual
                  </summary>
                  <code style={{ display: 'block', marginTop: 6, fontSize: 12, wordBreak: 'break-all' }}>
                    {iniciado.secret}
                  </code>
                </details>
                <p style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>2. Digite o código de 6 dígitos</p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="form-control"
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••••"
                  style={{ letterSpacing: 6, textAlign: 'center' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="ghost" onClick={adiar}>Agora não</Button>
                  <Button
                    variant="primary"
                    onClick={confirmar}
                    disabled={ocupado || codigo.length !== 6}
                    style={{ flex: 1 }}
                  >
                    {ocupado ? 'Confirmando…' : 'Confirmar e ativar'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
